import { Component, inject, signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ReferenceApiService } from '../../core/api/reference-api.service';

function atLeastOne(control: AbstractControl) {
  return Array.isArray(control.value) && control.value.length > 0 ? null : { required: true };
}

@Component({
  selector: 'op-create-service-category-page',
  imports: [
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
  ],
  templateUrl: './create-service-category.page.html',
  styleUrl: './create-service-category.page.scss',
})
export class CreateServiceCategoryPage {
  private readonly fb = inject(FormBuilder);
  private readonly referenceApi = inject(ReferenceApiService);

  readonly saving = signal(false);
  readonly error  = signal<string | null>(null);
  readonly saved  = signal(false);
  readonly savedId = signal<string | null>(null);

  // ── Chip input buffers ───────────────────────────────────────────────────────
  readonly exampleInput  = signal('');
  readonly confirmInput  = signal('');

  // ── Static options ────────────────────────────────────────────────────────────
  readonly VERTICAL_OPTIONS = [
    { value: 'auto',     label: 'Auto'     },
    { value: 'beauty',   label: 'Beauty'   },
    { value: 'cleaning', label: 'Cleaning' },
    { value: 'home',     label: 'Home'     },
  ];

  readonly SERVICE_MODEL_OPTIONS = [
    {
      value: 'managed_dispatch',
      label: 'Managed Dispatch',
      hint: 'Eldara collects payment and manages fulfilment',
    },
    {
      value: 'referral_dispatch',
      label: 'Referral Dispatch',
      hint: 'Eldara sends a referral; client pays provider directly',
    },
  ];

  // ── Form ──────────────────────────────────────────────────────────────────────
  readonly form = this.fb.nonNullable.group({
    name:                   ['', [Validators.required, Validators.maxLength(200)]],
    vertical:               ['', [Validators.required]],
    description_ai:         ['', [Validators.required, Validators.maxLength(3000)]],
    example_requests:       [[] as string[]],
    confirmation_questions: [[] as string[]],
    service_models:         [['managed_dispatch'] as string[], [atLeastOne]],
    is_active:              [false],
    category_id:            ['', [Validators.maxLength(100)]],
  });

  private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });

  readonly formInvalid = computed((): boolean => {
    this.formStatus();
    return this.form.invalid;
  });

  // Auto-generate category_id slug from name + vertical
  readonly autoSlug = computed(() => {
    this.formStatus();
    const name = this.form.controls.name.value;
    const vert = this.form.controls.vertical.value;
    if (!name || !vert) return '';
    return `${vert}_${name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')}`;
  });

  // ── Chip helpers ─────────────────────────────────────────────────────────────

  addExample(): void {
    const val = this.exampleInput().trim();
    if (!val) return;
    const cur = this.form.controls.example_requests.value;
    if (!cur.includes(val)) {
      this.form.controls.example_requests.setValue([...cur, val]);
    }
    this.exampleInput.set('');
  }

  removeExample(i: number): void {
    const cur = [...this.form.controls.example_requests.value];
    cur.splice(i, 1);
    this.form.controls.example_requests.setValue(cur);
  }

  addConfirm(): void {
    const val = this.confirmInput().trim();
    if (!val) return;
    const cur = this.form.controls.confirmation_questions.value;
    if (!cur.includes(val)) {
      this.form.controls.confirmation_questions.setValue([...cur, val]);
    }
    this.confirmInput.set('');
  }

  removeConfirm(i: number): void {
    const cur = [...this.form.controls.confirmation_questions.value];
    cur.splice(i, 1);
    this.form.controls.confirmation_questions.setValue(cur);
  }

  // ── Service model checkbox toggle ─────────────────────────────────────────────

  toggleModel(value: string): void {
    const cur = [...this.form.controls.service_models.value];
    const idx = cur.indexOf(value);
    if (idx >= 0) {
      cur.splice(idx, 1);
    } else {
      cur.push(value);
    }
    this.form.controls.service_models.setValue(cur);
  }

  isModelSelected(value: string): boolean {
    return this.form.controls.service_models.value.includes(value);
  }

  // ── Submit ────────────────────────────────────────────────────────────────────

  async submit(): Promise<void> {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.saving()) return;
    this.saving.set(true);
    this.error.set(null);
    const v = this.form.getRawValue();
    try {
      const result = await this.referenceApi.createAdminCategory({
        name:                   v.name.trim(),
        vertical:               v.vertical,
        description_ai:         v.description_ai.trim(),
        example_requests:       v.example_requests,
        confirmation_questions: v.confirmation_questions,
        is_active:              v.is_active,
        service_models:         v.service_models,
        category_id:            v.category_id.trim() || undefined,
      });
      this.savedId.set(result.category_id);
      this.saved.set(true);
    } catch (err: any) {
      this.error.set(err?.message ?? 'Failed to create service category. Please try again.');
    } finally {
      this.saving.set(false);
    }
  }

  addAnother(): void {
    this.form.reset({
      name: '', vertical: '', description_ai: '',
      example_requests: [], confirmation_questions: [],
      service_models: ['managed_dispatch'],
      is_active: false, category_id: '',
    });
    this.form.markAsUntouched();
    this.exampleInput.set('');
    this.confirmInput.set('');
    this.saved.set(false);
    this.savedId.set(null);
    this.error.set(null);
  }

  onExampleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') { e.preventDefault(); this.addExample(); }
  }

  onConfirmKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') { e.preventDefault(); this.addConfirm(); }
  }
}